import {SortingDropdown} from '@github-ui/dashboard-sorting-dropdown/SortingDropdown'
import {ListView} from '@github-ui/list-view'
import {ListViewDensityToggle} from '@github-ui/list-view/ListViewDensityToggle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'

import styles from '../shared/SharedListContainer.module.css'

import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {useAnalytics} from '@github-ui/use-analytics'
import {
  DEFAULT_SORTING_KEY,
  defaultSortingOptions,
  issuesDashboardSortingOptions,
} from '../shared/consts/sorting-consts'
import type {ElementType} from 'react'

import type {EnrichedIssue} from './types/list-types'
import type {EnrichedPull} from '../pulls/types/list-types'
import {IssueListItem} from './IssueListItem'
import {PullsListItem} from '../pulls/PullsListItem'
import {SharedDashboardBlankslate} from '../shared/SharedDashboardBlankslate'
import type {EmptyListState} from '../shared/types/blankslate-types'
import {DisplayMode, type FilterSelectType, type PageInfo} from '../shared/types/list-types'

// The issues dashboard renders a true discriminated union: PR rows require an
// explicit `itemType: 'pull_request'` (the backend always sets it). A type guard
// keeps the dispatch sound and lets the else-branch narrow cleanly to
// `EnrichedIssue`; anything without the PR discriminator uses the issue row,
// which reads a strict subset of fields and degrades more gracefully.
type MixedListRow = EnrichedIssue | (EnrichedPull & {itemType: 'pull_request'})

function isPullRequestItem(item: MixedListRow): item is EnrichedPull & {itemType: 'pull_request'} {
  return item.itemType === 'pull_request'
}

// Collision-free row key. Prefers the backend `globalId` (unique across issues
// and PRs). Falls back to the composite `${itemType}-${id}` — also collision-
// safe across the two id spaces — as a safety net for backend deploy skew
// where a payload might transiently omit `globalId`.
function rowKey(item: MixedListRow): string {
  return item.globalId ?? `${item.itemType}-${item.id}`
}

interface IssuesListProps {
  issues: MixedListRow[]
  pageInfo: PageInfo
  compactMode: boolean
  onCompactModeChange: (value: boolean) => void
  onSortingItemSelect: (key: string) => void
  onStateFilterSelect: (state: string) => void
  emptyListState: EmptyListState
  sortingItemSelected?: string
  stateItemSelected?: string
  onFilterSelect?: (type: FilterSelectType, value: string) => void
  isDeferredLoading?: boolean
  isAgentAttributionEnabled?: boolean
  /** Builds a query-preserving URL that filters the list by an issue type name. */
  getIssueTypeHref: (issueTypeName: string) => string
  /** Optional client-router link component for issue type tokens (SPA navigation). */
  issueTypeLinkComponent?: ElementType
}

export function IssuesList({
  issues,
  pageInfo,
  compactMode,
  onCompactModeChange,
  sortingItemSelected,
  onSortingItemSelect,
  stateItemSelected,
  onStateFilterSelect,
  emptyListState,
  onFilterSelect,
  isDeferredLoading,
  isAgentAttributionEnabled,
  getIssueTypeHref,
  issueTypeLinkComponent,
}: IssuesListProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  const resultText = pageInfo.totalCount === 1 ? 'result' : 'results'

  return (
    <ListView
      title="Issues"
      titleHeaderTag="h2"
      className={styles.listContainer}
      onVariantChange={value => onCompactModeChange(value === DisplayMode.Compact)}
      variant={compactMode ? DisplayMode.Compact : DisplayMode.Default}
      metadata={
        <ListViewMetadata
          title={
            stateItemSelected === 'open' || stateItemSelected === 'closed' ? undefined : (
              <span data-testid="results-count">
                {pageInfo.totalCount} {resultText}
              </span>
            )
          }
          sectionFilters={
            stateItemSelected === 'open' || stateItemSelected === 'closed' ? (
              <>
                <ListViewSectionFilterLink
                  key="open"
                  title="Open"
                  href="#"
                  onClick={e => {
                    e.preventDefault()
                    sendAnalyticsEvent('issues-dashboard.click', 'STATE_FILTER', {state: 'open'})
                    onStateFilterSelect('open')
                  }}
                  isSelected={stateItemSelected === 'open'}
                  count={stateItemSelected === 'open' ? pageInfo.totalCount : undefined}
                />
                <ListViewSectionFilterLink
                  key="closed"
                  title="Closed"
                  href="#"
                  onClick={e => {
                    e.preventDefault()
                    sendAnalyticsEvent('issues-dashboard.click', 'STATE_FILTER', {state: 'closed'})
                    onStateFilterSelect('closed')
                  }}
                  isSelected={stateItemSelected === 'closed'}
                  count={stateItemSelected === 'closed' ? pageInfo.totalCount : undefined}
                />
              </>
            ) : undefined
          }
          densityToggle={<ListViewDensityToggle />}
        >
          <div className="mr-2">
            <SortingDropdown
              sortingItemSelected={sortingItemSelected || ''}
              onSortingItemSelect={onSortingItemSelect}
              defaultSortingKey={DEFAULT_SORTING_KEY}
              sortingOptions={{...defaultSortingOptions, ...issuesDashboardSortingOptions}}
              directionRestrictedKeys={['relevance']}
              sortFieldsRequireDescSuffix
            />
          </div>
        </ListViewMetadata>
      }
    >
      {issues.length > 0 ? (
        issues.map(item =>
          isPullRequestItem(item) ? (
            <PullsListItem
              key={rowKey(item)}
              pull={item}
              onFilterSelect={onFilterSelect}
              isDeferredLoading={isDeferredLoading}
              isAgentAttributionEnabled={isAgentAttributionEnabled}
              sortingItemSelected={sortingItemSelected}
            />
          ) : (
            <IssueListItem
              key={rowKey(item)}
              issue={item}
              onFilterSelect={onFilterSelect}
              getIssueTypeHref={getIssueTypeHref}
              issueTypeLinkComponent={issueTypeLinkComponent}
              sortingItemSelected={sortingItemSelected}
            />
          ),
        )
      ) : (
        <li>
          <SharedDashboardBlankslate dashboardType="issues" variant="empty" emptyListState={emptyListState} />
        </li>
      )}
    </ListView>
  )
}
